import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useCurrUserCtx } from './CurrUserContext';
import { useUserCtx } from './UserContext';
import { isAdmin } from '../utils/user.util';
import { setImpersonationGetter } from '../dataExchange/radqcApiClient';

const ImpersonationContext = createContext();

function ImpersonationCtxProvider(props) {
  const { children } = props;
  const { currUser, updateCurrUser } = useCurrUserCtx();
  const { users, updateUsers } = useUserCtx();

  // Restore impersonation state from sessionStorage on mount
  const [impersonatingUser, setImpersonatingUser] = useState(() => {
    try {
      const stored = sessionStorage.getItem('impersonatingUser');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // Track if we've handled the initial restoration
  const hasRestoredInitialState = useRef(false);

  // Check if current user is god and can impersonate (only god role, not admin)
  // Use optional chaining to handle cases where currUser might be null during navigation
  const canImpersonate = currUser?.role === 'god';

  // Register the getter function with the API client and persist state
  useEffect(() => {
    setImpersonationGetter(() => impersonatingUser?.email || null);
    // Persist impersonation state to sessionStorage
    if (impersonatingUser) {
      sessionStorage.setItem('impersonatingUser', JSON.stringify(impersonatingUser));
    } else {
      sessionStorage.removeItem('impersonatingUser');
    }
  }, [impersonatingUser]);

  // Handle initial impersonation state restoration (only once)
  useEffect(() => {
    if (!hasRestoredInitialState.current && impersonatingUser && currUser) {
      hasRestoredInitialState.current = true;
      // Small delay to ensure the API client has updated the header
      const timer = setTimeout(() => {
        updateCurrUser();
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // REMOVED the problematic useEffect that depended on currUser and updateCurrUser
  // Updates are now handled explicitly in startImpersonating/stopImpersonating

  const startImpersonating = useCallback(
    (user) => {
      if (!canImpersonate) {
        console.error('User does not have permission to impersonate');
        return;
      }
      // Set the getter immediately so API calls include the header
      setImpersonationGetter(() => user?.email || null);
      setImpersonatingUser(user);
      // Refresh users list to get updated context
      updateUsers();
      // Update current user after a brief delay to ensure header is set
      setTimeout(() => {
        updateCurrUser();
      }, 100);
    },
    [canImpersonate, updateUsers, updateCurrUser],
  );

  const stopImpersonating = useCallback(() => {
    // Clear the getter immediately
    setImpersonationGetter(() => null);
    setImpersonatingUser(null);
    // Refresh both users list and current user to get updated context
    updateUsers();
    // Small delay to ensure the API client has cleared the header before refreshing
    setTimeout(() => {
      updateCurrUser();
    }, 100);
  }, [updateUsers, updateCurrUser]);

  return (
    <ImpersonationContext.Provider
      value={{
        impersonatingUser,
        startImpersonating,
        stopImpersonating,
        canImpersonate,
        isImpersonating: !!impersonatingUser,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}

const useImpersonationCtx = () => {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonationCtx must be used within an ImpersonationCtxProvider');
  }
  return context;
};

export { ImpersonationCtxProvider, useImpersonationCtx };
